import { Capacitor } from '@capacitor/core';
import { isBundledMobileApp } from '@/mobile/native/apiBase';
import { databaseService } from '@/services/database/DatabaseService';
import type { FieldProjectListItem, FieldReportListItem } from '@/modules/field/types';

const REPORTS_MIGRATED_META = 'field_cache_reports_sqlite_v1';

async function ensureFieldCacheTables(): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  await databaseService.init();
}

function useSqliteReportsStore(): boolean {
  return isBundledMobileApp() && Capacitor.isNativePlatform();
}

export async function isFieldReportsSqliteActive(): Promise<boolean> {
  if (!useSqliteReportsStore()) {
    return false;
  }

  return (await databaseService.getMeta(REPORTS_MIGRATED_META)) === '1';
}

export async function clearFieldListSqlite(): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  await ensureFieldCacheTables();

  await databaseService.runQuery(`DELETE FROM field_cache_reports`);
  await databaseService.runQuery(`DELETE FROM field_cache_projects`);
  await databaseService.setMeta(REPORTS_MIGRATED_META, '');
}

export async function replaceFieldReportsInSqlite(
  reports: FieldReportListItem[],
): Promise<void> {
  if (!useSqliteReportsStore()) {
    return;
  }

  await ensureFieldCacheTables();

  await databaseService.runQuery(`DELETE FROM field_cache_reports`);

  for (const report of reports) {
    await upsertFieldReportInSqlite(report);
  }

  await databaseService.setMeta(REPORTS_MIGRATED_META, '1');
}

export async function pruneFieldReportsInSqlite(keepReportIds: Set<number>): Promise<void> {
  if (!useSqliteReportsStore() || keepReportIds.size === 0) {
    return;
  }

  await ensureFieldCacheTables();

  const rows = await databaseService.queryRows<Record<string, unknown>>(
    `SELECT report_id FROM field_cache_reports`,
  );

  for (const row of rows) {
    const id = Number(row.report_id);
    if (!keepReportIds.has(id)) {
      await databaseService.runQuery(`DELETE FROM field_cache_reports WHERE report_id = ?`, [
        id,
      ]);
    }
  }
}

export async function upsertFieldReportInSqlite(report: FieldReportListItem): Promise<void> {
  if (!useSqliteReportsStore()) {
    return;
  }

  await ensureFieldCacheTables();

  const syncStatus = report.sync === 'not_synced' ? 'not_synced' : 'synced';

  await databaseService.runQuery(
    `INSERT OR REPLACE INTO field_cache_reports
     (report_id, client_uuid, report_date, sync_status, payload_json, updated_at)
     VALUES (?, ?, ?, ?, ?, ?)`,
    [
      report.id,
      report.clientUuid ?? null,
      report.reportDateIso ?? '',
      syncStatus,
      JSON.stringify(report),
      new Date().toISOString(),
    ],
  );
}

export async function deleteFieldReportsInSqlite(options: {
  ids?: number[];
  clientUuids?: string[];
}): Promise<void> {
  if (!useSqliteReportsStore()) {
    return;
  }

  await ensureFieldCacheTables();

  for (const id of options.ids ?? []) {
    await databaseService.runQuery(`DELETE FROM field_cache_reports WHERE report_id = ?`, [
      id,
    ]);
  }

  for (const uuid of options.clientUuids ?? []) {
    await databaseService.runQuery(
      `DELETE FROM field_cache_reports WHERE client_uuid = ?`,
      [uuid],
    );
  }
}

export async function loadFieldReportsFromSqlite(): Promise<FieldReportListItem[]> {
  if (!useSqliteReportsStore()) {
    return [];
  }

  await ensureFieldCacheTables();

  const active = await isFieldReportsSqliteActive();
  if (!active) {
    return [];
  }

  const rows = await databaseService.queryRows<Record<string, unknown>>(
    `SELECT payload_json FROM field_cache_reports ORDER BY report_date DESC, report_id DESC`,
  );

  const reports: FieldReportListItem[] = [];

  for (const row of rows) {
    try {
      reports.push(JSON.parse(String(row.payload_json)) as FieldReportListItem);
    } catch {
      // ignora linha corrompida
    }
  }

  return reports;
}

export async function replaceFieldProjectsInSqlite(
  projects: FieldProjectListItem[],
): Promise<void> {
  if (!useSqliteReportsStore()) {
    return;
  }

  await ensureFieldCacheTables();

  await databaseService.runQuery(`DELETE FROM field_cache_projects`);

  for (const project of projects) {
    await databaseService.runQuery(
      `INSERT OR REPLACE INTO field_cache_projects (project_id, payload_json, updated_at)
       VALUES (?, ?, ?)`,
      [project.id, JSON.stringify(project), new Date().toISOString()],
    );
  }
}

export async function loadFieldProjectsFromSqlite(): Promise<FieldProjectListItem[]> {
  if (!useSqliteReportsStore()) {
    return [];
  }

  await ensureFieldCacheTables();

  const rows = await databaseService.queryRows<Record<string, unknown>>(
    `SELECT payload_json FROM field_cache_projects ORDER BY project_id ASC`,
  );

  const projects: FieldProjectListItem[] = [];

  for (const row of rows) {
    try {
      projects.push(JSON.parse(String(row.payload_json)) as FieldProjectListItem);
    } catch {
      // ignora
    }
  }

  return projects;
}
